import Vue from 'vue';
import Vuex from 'vuex';
import VueRouter from 'vue-router';
import VeeValidate from 'vee-validate';
import { default as VueModal } from 'vue-js-modal';
import Cookies from 'js-cookie';

import { initialize } from '../../util/sentry';
initialize(Vue);

import Shippings from '../../stores/schema/Shipping';
import ShoppingCarts from '../../stores/schema/ShoppingCarts';
import { Payment as Payments } from '../../stores/schema/Payment';

import userStore from '../../stores/modules/user';
import shoppingCartStore from '../../stores/modules/shoppingcart';
import shoppingCouponsStore from '../../stores/modules/shoppingCoupons';
import shoppingDiscountsStore from '../../stores/modules/shoppingDiscounts';
import shippingsStore from '../../stores/modules/shippings';
import shipmentsStore from '../../stores/modules/shipments';
import paymentsStore from '../../stores/modules/payments';
import premiumPlansStore from '../../stores/modules/premiumPlans';

import { SHOPPING_CART as shoppingCartTypes } from '../../stores/mutation-types';

import checkoutIndexPage from '../../pages/checkout/index.vue';
import checkoutShippingsIndexPage from '../../pages/checkout/shippings/index.vue';
import checkoutShippingsEditPage from '../../pages/checkout/shippings/edit.vue';
import checkoutPaymentsIndexPage from '../../pages/checkout/payments/index.vue';
import checkoutReviewPage from '../../pages/checkout/review/index.vue';

import pointsComponent from '../../components/checkout/points.vue';
import couponsComponent from '../../components/checkout/coupons.vue';

import textUtil from '../../util/text';

import Dialog from '../../plugins/dialog';

Vue.use(Vuex);
Vue.use(VueRouter);
Vue.use(VeeValidate, {
  events: 'change|blur',
  classes: true,
  classNames: {
    valid: 'form-ctl--valid',
    invalid: 'form-ctl--error'
  }
});
Vue.use(VueModal, {
  dynamic: true
});
Vue.use(Dialog);

const stepHistories = [];
const scrollPositionHistories = {};

const redirect = function (path) {
  window.location.href = path;
};

const store = new Vuex.Store({
  strict: process.env.NODE_ENV !== 'production',
  state: {
    storeLoaded: false
  },
  mutations: {
    storeLoaded(state) {
      state.storeLoaded = true;
    }
  },
  modules: {
    user: userStore,
    cart: shoppingCartStore,
    shoppingCoupons: shoppingCouponsStore,
    shoppingDiscounts: shoppingDiscountsStore,
    shippings: shippingsStore,
    shipments: shipmentsStore,
    payments: paymentsStore,
    premiumPlans: premiumPlansStore
  }
});

const isSmartPhone = store.state.user.is_smart_phone_device;

const baseRoutes = {
  name: 'checkout',
  path: '/checkout',
  component: checkoutIndexPage,
  meta: {
    requiresAuth: true,
    title: 'Order Confirmation'
  },
  children: [
    {
      name: 'select_shipping_address',
      path: 'shippings',
      component: checkoutShippingsIndexPage,
      meta: {
        step: 2,
        title: 'Select a Shipping Address'
      }
    },
    {
      name: 'input_shipping_address',
      path: 'shippings/:id',
      component: checkoutShippingsEditPage,
      meta: {
        step: 2,
        title: 'Input a Shipping Address'
      }
    },
    {
      name: 'select_payment_method',
      path: 'payments',
      component: checkoutPaymentsIndexPage,
      props: {
        stripeKey: window.propsData.stripeKey,
        recaptchaSiteKey: window.propsData.recaptchaSiteKey
      },
      meta: {
        step: 3,
        title: 'Select a Payment Method'
      }
    },
    {
      name: 'review_order',
      path: 'review',
      component: checkoutReviewPage,
      props: {
        switchUser: window.propsData.switchUser,
        hasValidEmail: window.propsData.hasValidEmail,
        messageForDelivery: window.propsData.messageForDelivery
      },
      meta: {
        step: 4,
        title: 'Review Your Order'
      }
    }
  ]
};

const reviewRoutes = baseRoutes.children.find((route) => {
  return route.name === 'review_order';
});

// モバイル版
if (isSmartPhone) {
  // レビュー画面へ遷移する際にはスクロール位置を復帰させる
  reviewRoutes.meta.restoreScrollPosition = true;

  const routes = [
    // ポイント
    {
      name: 'points',
      path: 'review/points',
      component: pointsComponent,
      meta: {
        title: 'Points'
      }
    },
    // クーポン
    {
      name: 'coupons',
      path: 'review/coupons',
      component: couponsComponent,
      meta: {
        title: 'Coupons'
      }
    }
  ];

  baseRoutes.children.push(...routes);
}

// デスクトップ版
if (!isSmartPhone) {
  // レビュー画面へ遷移する際にはスクロール位置を復帰させない
  reviewRoutes.meta.restoreScrollPosition = false;

  reviewRoutes.children = [
    {
      name: 'points',
      path: 'points',
      component: pointsComponent,
      meta: {
        title: 'Points',
        noopScrollPosition: true,
        openModal: true
      }
    },
    {
      name: 'coupons',
      path: 'coupons',
      component: couponsComponent,
      meta: {
        title: 'Coupons',
        noopScrollPosition: true,
        openModal: true
      }
    }
  ];
}

// 404
baseRoutes.children.push({
  name: 'not_found',
  path: '*',
  redirect: () => {
    redirect('/cart');
  }
});

const router = new VueRouter({
  mode: 'history',
  routes: [baseRoutes],
  scrollBehavior(to, from, savedPosition) {
    // savedPositionが存在すれば優先して使用
    // ページのnext/back時に復元する
    if (savedPosition) {
      return savedPosition;
    }

    // スクロール位置を復元する設定がtrueになっている場合
    if (to.meta.restoreScrollPosition) {
      return scrollPositionHistories[to.name];
    }

    // 遷移時にスクロール位置を操作しない設定がtrueになっている場合
    if (to.meta.noopScrollPosition || from.meta.noopScrollPosition) {
      return false;
    }

    return {
      x: 0,
      y: 0
    };
  }
});

const sendGACheckout = (step, once) => {
  // Google Analytics 拡張eコマースにデータを送信
  if (!ShoppingCarts.isEmpty(store.getters['cart/current']())) {
    const productList = [];
    const cart = store.getters['cart/current']({
      populate: ['shipments']
    });
    cart.shipments.forEach((shipment) => {
      shipment.items.forEach((item) => {
        const options = {
          id: item.product,
          name: item.sku.titles.en_us,
          variant: item.sku.short_titles.en_us || item.sku.titles.en_us,
          price: item.sku.purchase_price,
          quantity: item.qty
        };
        // Bland
        // Categories
        productList.push(options);
      });
    });

    // 決済プロセス:1はセッション内で1度のみ送信
    const alreadySendStep1 =
      stepHistories.filter((history) => {
        return history.step === 1;
      }).length && step === 1;

    // 重複した決済プロセスは自動送信しない
    const alreadySendStep =
      stepHistories.filter((history) => {
        return history.step === step;
      }).length && once;

    // 自動送信された決済プロセスは1度のみ送信
    const alreadySendStepAuto = stepHistories.filter((history) => {
      return history.step === step && history.once === once && history.once;
    }).length;

    if (alreadySendStep1 || alreadySendStep || alreadySendStepAuto) {
      return;
    }

    stepHistories.push({
      step: step,
      once: once
    });

    let coupon = '';
    if (store.getters['shoppingCoupons/list'].length) {
      coupon = store.getters['shoppingCoupons/list'][0].display_name;
    }

    window.tom.helper.ga.checkout(
      productList,
      {
        coupon,
        value: store.getters['cart/current']().grand_total,
        step: step
      },
      {
        currencyCode: 'USD'
      }
    );
  }
};

const nextCheckoutStep = function (to, from, next) {
  const cart = store.getters['cart/current']() || {};
  const query = {
    wl: cart.wishlistId
  };

  if (ShoppingCarts.isEmpty(cart)) {
    return redirect(
      '/cart' + (cart.wishlistId ? `?wl=${cart.wishlistId}` : '')
    );
  }

  // 前回の画面のスクロール位置を保存
  // 履歴が存在する場合のみ保存
  if (from) {
    scrollPositionHistories[from.name] = {
      x: document.documentElement.scrollLeft,
      y: document.documentElement.scrollTop
    };
  }

  // `/checkout`経由でのアクセス
  if (to.name === 'checkout') {
    // 配送先住所が設定されていない場合
    // ただしウィッシュリスト経由の購入は除く
    if (!ShoppingCarts.isGiftFromWishList(cart) && !Shippings.getCurrent()) {
      return next({
        name: 'input_shipping_address',
        params: {
          id: 'new'
        },
        query
      });
    }

    // 決済情報が設定されていない場合
    if (!Payments.getCurrent()) {
      return next({
        name: 'select_payment_method',
        query
      });
    }

    // 配送先住所と決済情報が設定されている場合
    return next({
      name: 'review_order',
      query
    });
  }

  // ウィッシュリスト経由の購入時に他画面に遷移する場合
  // 例: /checkout/review?wl=XXX -> /checkout/payments
  if (query.wl && !to.query.wl && from.name !== to.name) {
    return next({
      name: to.name,
      params: to.params,
      query
    });
  }

  // イレギュラーケース
  if (
    // ウィッシュリスト経由の購入は配送先住所の登録・編集画面に遷移できないようにした
    (ShoppingCarts.isGiftFromWishList(cart) &&
      ['select_shipping_address', 'input_shipping_address'].includes(
        to.name
      )) ||
    // Manual Order経由の購入は決済情報の登録・選択画面に遷移できないようにした
    (Payments.isBypass(Payments.getCurrent()) &&
      !!window.propsData.switchUser &&
      to.name === 'select_payment_method')
  ) {
    return nextCheckoutStep(
      {
        name: 'checkout'
      },
      from,
      next
    );
  }

  sendGACheckout(1);

  // すでに配送先住所が設定されている場合は決済プロセス:2を自動送信
  if (Shippings.getCurrent()) {
    sendGACheckout(2, true);
  }

  // すでに決済情報が設定されている場合は決済プロセス:3を自動送信
  if (Payments.getCurrent()) {
    sendGACheckout(3, true);
  }

  // 配送先住所選択・登録画面へのアクセス
  if (['select_shipping_address', 'input_shipping_address'].includes(to.name)) {
    sendGACheckout(2);
  }

  // 決済情報選択・登録画面へのアクセス
  if (['select_payment_method'].includes(to.name)) {
    sendGACheckout(3);
  }

  next();
};

/**
 * ナビゲーションガード
 */
router.beforeEach((to, from, next) => {
  // ログイン必須なフロー配下で未ログインであればサインアップページへリダイレクト
  if (
    to.matched.some((r) => r.meta.requiresAuth) &&
    !store.state.user.is_login
  ) {
    let redirectUrl = `/signup?r=${to.fullPath}`;
    return redirect(redirectUrl);
  }

  // 初期ストアロードが完了していればそのまま表示処理
  if (store.state.storeLoaded) {
    return nextCheckoutStep(to, from, next);
  }

  // ストアが取得されていなければガードして、APIから取得処理を行う
  store.dispatch('shippings/setService', 'shop');
  store.dispatch('payments/setService', 'shop');

  // Shopping cart を取得
  store
    .dispatch('cart/fetch', {
      wishlist_id: to.query.wl
    })
    .then((res) => {
      // ログインしていれば住所情報と決済情報を取得
      if (store.state.user.is_login) {
        store.dispatch('user/setRevivableRight', {
          revivable_right: res.revivableRight
        });
        return Promise.all([
          store.dispatch('shippings/fetchCurrent'),
          store.dispatch('payments/fetchCurrent')
        ]);
      }
      return Promise.resolve();
    })
    .catch((err) => {
      return redirect(`/cart?err=${encodeURIComponent(err)}`);
    })
    .then(() => {
      store.commit('storeLoaded');
      nextCheckoutStep(to, from, next);
    });
});

router.afterEach((to) => {
  document.title = to.meta.title + ' | Tokyo Otaku Mode';

  // レビュー画面にアクセス、購入可能な状態なら決済プロセス:4を送信する
  const cart = store.getters['cart/current']({
    populate: ['shipments']
  });
  const user = store.state.user;

  if (to.name === 'review_order' && ShoppingCarts.canPurchase(cart, user)) {
    sendGACheckout(4, true);
  }

  // モーダルで開く設定になっている場合
  if (to.meta.openModal) {
    const props = {};

    // Shipment IDをpropsとして定義する
    if (to.params.shipment_id) {
      props.shipmentId = to.params.shipment_id;
    }

    const [modalComponent] = router.getMatchedComponents(to).slice(-1);

    return router.app.$nextTick(() => {
      router.app.$modal.show(modalComponent, props, {
        width: '90%',
        height: 'auto',
        maxWidth: 960,
        adaptive: true,
        scrollable: true,
        clickToClose: false
      });
    });
  }
});

store.subscribe((mutation) => {
  const cart = store.getters['cart/current']({ populate: ['shipments'] });
  const user = store.state.user;

  // 下記操作時に購入可能状態になれば決済プロセス:4を都度送信する
  // プレミアム未加入で決済方法を変更した際
  // プレミアム未加入でプレミアム入会チェックボックスを切り替えた際
  // 配送不可アイテムを削除した際
  if (
    mutation.type === `cart/${shoppingCartTypes.ENABLE_TO_PURCHASE}` &&
    ShoppingCarts.canPurchase(cart, user)
  ) {
    sendGACheckout(4);
  }

  // カートが空になった場合
  if (mutation.type === `cart/${shoppingCartTypes.UPDATE_SHIPMENTS}`) {
    if (!mutation.payload.shipments.length) {
      return redirect(
        '/cart' + (cart.wishlistId ? `?wl=${cart.wishlistId}` : '')
      );
    }
  }
  // カートのexpire時間過ぎた場合
  else if (
    mutation.type === `cart/${shoppingCartTypes.UPDATE_EXPIRATION_SECONDS}`
  ) {
    if (mutation.payload.expiration_seconds <= 0) {
      // MEMO: サーバーとの誤差があるかもしれないのでカートを再取得したほうがいいかもしれない
      return redirect(
        '/cart?err=cart_expired' +
          (cart.wishlistId ? `&wl=${cart.wishlistId}` : '')
      );
    }
  }
});

new Vue({
  el: '#vue-app',
  store,
  router,

  data() {
    return {
      cookieWelcomeCouponMessage: textUtil.toBoolean(
        Cookies.get('welcomeCouponMessage')
      )
    };
  },
  computed: {
    // Welcome Gift メッセージを表示するかどうか
    // 初回購入者でメッセージを閉じたクッキーがない、かつ住所入力と決済入力でのみ表示
    isDisplayWelcomeGiftMessage() {
      return (
        (this.$route.name === 'select_shipping_address' ||
          this.$route.name === 'input_shipping_address' ||
          this.$route.name === 'select_payment_method') &&
        this.$store.state.user.is_new_customer &&
        !this.cookieWelcomeCouponMessage
      );
    }
  },
  methods: {
    // Welcome Gift メッセージを閉じる
    closeWelcomeGiftMessage() {
      Cookies.set('welcomeCouponMessage', true, {
        path: '/',
        expires: 30
      });
      // cookie が変わってもバインドされないので、強制的に true に変更する
      this.cookieWelcomeCouponMessage = true;
    }
  }
});
